/**
 * User Notification Utilities
 * Provides consistent user feedback for errors and events
 */

import { tUnsafe } from '../i18n';

interface NotificationOptions {
  title?: string;
  duration?: number;
  type?: 'error' | 'success' | 'info';
}

type TranslationValues = Record<string, string | number>;

interface TranslatedNotificationOptions extends NotificationOptions {
  titleValues?: TranslationValues;
}

interface TranslatedMessageOptions {
  values?: TranslationValues;
}

type NotificationMessage = string | { key: string; values?: TranslationValues };

const NOTIFICATION_CONTAINER_ID = 'verityav-notification-container';
const NOTIFICATION_STYLE_ID = 'verityav-notification-styles';
const NOTIFICATION_REMOVE_DELAY_MS = 200;

const resolveMessage = (message: NotificationMessage): string => {
  if (typeof message === 'string') {
    return message;
  }

  return tUnsafe(message.key, message.values);
};

const ensureNotificationStyles = (doc: Document) => {
  if (doc.getElementById(NOTIFICATION_STYLE_ID)) {
    return;
  }

  const style = doc.createElement('style');
  style.id = NOTIFICATION_STYLE_ID;
  style.textContent = `
    #${NOTIFICATION_CONTAINER_ID} {
      position: fixed;
      top: 16px;
      right: 16px;
      z-index: 800;
      display: flex;
      flex-direction: column;
      gap: 12px;
      width: min(360px, calc(100vw - 32px));
      pointer-events: none;
    }

    .verityav-notification {
      pointer-events: auto;
      border-radius: 12px;
      border: 1px solid rgba(15, 23, 42, 0.12);
      background: #ffffff;
      box-shadow: 0 18px 40px rgba(15, 23, 42, 0.18);
      color: #0f172a;
      opacity: 0;
      transform: translateY(-8px);
      transition: opacity 0.2s ease, transform 0.2s ease;
      overflow: hidden;
    }

    .verityav-notification.is-visible {
      opacity: 1;
      transform: translateY(0);
    }

    .verityav-notification__accent {
      height: 4px;
      width: 100%;
    }

    .verityav-notification--error .verityav-notification__accent {
      background: #dc2626;
    }

    .verityav-notification--success .verityav-notification__accent {
      background: #16a34a;
    }

    .verityav-notification--info .verityav-notification__accent {
      background: #2563eb;
    }

    .verityav-notification__body {
      padding: 14px 16px 16px;
    }

    .verityav-notification__title {
      margin: 0 0 4px;
      font-size: 14px;
      font-weight: 700;
      line-height: 1.3;
    }

    .verityav-notification__message {
      margin: 0;
      font-size: 14px;
      line-height: 1.45;
    }

    @media (max-width: 480px) {
      #${NOTIFICATION_CONTAINER_ID} {
        left: 16px;
        right: 16px;
        width: auto;
      }
    }
  `;

  doc.head.appendChild(style);
};

const ensureNotificationContainer = (doc: Document): HTMLElement => {
  const existing = doc.getElementById(NOTIFICATION_CONTAINER_ID);
  if (existing) {
    return existing;
  }

  const container = doc.createElement('div');
  container.id = NOTIFICATION_CONTAINER_ID;
  container.setAttribute('aria-live', 'polite');
  container.setAttribute('aria-atomic', 'true');
  doc.body.appendChild(container);
  return container;
};

const showNotification = (
  type: NonNullable<NotificationOptions['type']>,
  title: string,
  message: string,
  duration = 5000
) => {
  if (typeof document === 'undefined') {
    return;
  }

  ensureNotificationStyles(document);
  const container = ensureNotificationContainer(document);

  const notification = document.createElement('div');
  notification.className = `verityav-notification verityav-notification--${type}`;
  notification.setAttribute('role', type === 'error' ? 'alert' : 'status');

  const accent = document.createElement('div');
  accent.className = 'verityav-notification__accent';

  const body = document.createElement('div');
  body.className = 'verityav-notification__body';

  const titleElement = document.createElement('p');
  titleElement.className = 'verityav-notification__title';
  titleElement.textContent = title;

  const messageElement = document.createElement('p');
  messageElement.className = 'verityav-notification__message';
  messageElement.textContent = message;

  body.appendChild(titleElement);
  body.appendChild(messageElement);
  notification.appendChild(accent);
  notification.appendChild(body);
  container.appendChild(notification);

  const scheduleFrame =
    typeof window.requestAnimationFrame === 'function'
      ? window.requestAnimationFrame.bind(window)
      : (callback: () => void) => window.setTimeout(callback, 0);

  scheduleFrame(() => {
    notification.classList.add('is-visible');
  });

  window.setTimeout(() => {
    notification.classList.remove('is-visible');
    window.setTimeout(() => {
      notification.remove();
      if (!container.childElementCount) {
        container.remove();
      }
    }, NOTIFICATION_REMOVE_DELAY_MS);
  }, duration);
};

/**
 * Show error notification to user
 */
export function showErrorNotification(
  message: NotificationMessage,
  options: TranslatedNotificationOptions = {}
) {
  const { title, titleValues, duration } = options;
  const resolvedTitle = title || tUnsafe('notifications.errorTitle', titleValues);
  const resolvedMessage = resolveMessage(message);

  showNotification('error', resolvedTitle, resolvedMessage, duration);

  // Log error in development
  if (process.env.NODE_ENV === 'development') {
    console.error(`[Notification] ${resolvedTitle}:`, resolvedMessage);
  }
}

/**
 * Show success notification to user
 */
export function showSuccessNotification(
  message: NotificationMessage,
  options: TranslatedNotificationOptions = {}
) {
  const { title, titleValues, duration } = options;
  const resolvedTitle = title || tUnsafe('notifications.successTitle', titleValues);
  const resolvedMessage = resolveMessage(message);

  showNotification('success', resolvedTitle, resolvedMessage, duration);
}

/**
 * Show info notification to user
 */
export function showInfoNotification(
  message: NotificationMessage,
  options: TranslatedNotificationOptions = {}
) {
  const { title, titleValues, duration } = options;
  const resolvedTitle = title || tUnsafe('notifications.infoTitle', titleValues);
  const resolvedMessage = resolveMessage(message);

  showNotification('info', resolvedTitle, resolvedMessage, duration);

  if (process.env.NODE_ENV === 'development') {
    console.info(`[Notification] ${resolvedTitle}:`, resolvedMessage);
  }
}

/**
 * Format error message for user display
 */
export function formatErrorMessage(error: unknown, options: TranslatedMessageOptions = {}): string {
  if (error instanceof Error) {
    // Common error type mappings
    if (error.name === 'NotAllowedError') {
      return tUnsafe('notifications.cameraAccessDenied', options.values);
    }
    if (error.name === 'NotFoundError') {
      return tUnsafe('notifications.noCameraFound', options.values);
    }
    if (error.name === 'NotReadableError') {
      return tUnsafe('notifications.cameraInUse', options.values);
    }
    if (error.message.includes('network')) {
      return tUnsafe('notifications.networkError', options.values);
    }

    // Default to error message if it's user-friendly
    if (error.message.length < 100 && !error.message.includes('stack')) {
      return error.message;
    }
  }

  // Generic error message
  return tUnsafe('notifications.unexpectedError', options.values);
}
